import { useCallback, useEffect, useMemo, useState, useSyncExternalStore } from "react";
import type { EncryptionMode } from "@vmeste/shared";
import { keysVersion, loadSpaceKey, subscribeToKeys } from "../lib/spacekeys.js";
import { syncEngine } from "../lib/sync.js";
import { useSession } from "./session.js";

/**
 * Два вопроса, которые экранам приходится задавать про шифрование: «заперто ли
 * это пространство» и «есть ли на этом устройстве ключ».
 *
 * Оба — здесь, а не в компонентах, потому что ответ на каждый собирается из
 * двух источников и легко собирается неправильно.
 */

/**
 * Режим пространства.
 *
 * Состояние синхронизации знает свежее — оно приходит дельтой, в том числе
 * когда шифрование включил другой человек с другого телефона. Но на холодном
 * старте дельты ещё нет, а решать про замок надо до первого экрана; тогда
 * отвечает сессия, которую `me` приносит первой.
 */
export function useEncryptionMode(spaceId: string | null): EncryptionMode {
  const { session } = useSession();
  const snapshot = useSyncExternalStore(syncEngine.subscribe, syncEngine.getSnapshot);

  return useMemo(() => {
    if (!spaceId) return "none";

    const synced = syncEngine.view(spaceId).space?.encryptionMode;
    if (synced) return synced;

    return session?.spaces.find((space) => space.id === spaceId)?.encryptionMode ?? "none";
  }, [snapshot, session, spaceId]);
}

/** Пока хранилище ключей не ответило, про устройство не известно ничего. */
export type KeyPresence = "checking" | "present" | "absent";

/**
 * Есть ли у этого устройства ключ от пространства.
 *
 * Спрашивается у хранилища ключей напрямую, а не у `syncEngine.codec`, и это
 * важно: кодек знает про ключ только после первой дельты, а до неё выдаёт
 * обычный, нешифрующий. Экран, построенный на нём, показал бы замок человеку, у
 * которого ключ есть, — каждый раз при запуске приложения, и ровно в том месте,
 * где такое читается как «ключ потерян».
 *
 * Хранилище ключей само сообщает о переменах, и подписаны на него **все**
 * спрашивающие. Иначе ответ вычислялся бы по разу на компонент и больше
 * никогда: экран, включивший шифрование, про новый ключ знал, а обёрнутый
 * вокруг него layout — нет, и показывал замок на устройстве, где ключ лежит,
 * прося код восстановления, выданный десять секунд назад.
 *
 * `recheck` остался для случая, когда ключ мог появиться мимо хранилища —
 * например, его положила другая вкладка.
 */
export function useSpaceKey(spaceId: string | null): {
  presence: KeyPresence;
  recheck: () => void;
} {
  const [presence, setPresence] = useState<KeyPresence>("checking");
  const [attempt, setAttempt] = useState(0);
  const keys = useSyncExternalStore(subscribeToKeys, keysVersion);

  useEffect(() => {
    if (!spaceId) {
      setPresence("absent");
      return;
    }

    let alive = true;
    setPresence("checking");

    void loadSpaceKey(spaceId).then((key) => {
      if (alive) setPresence(key ? "present" : "absent");
    });

    return () => {
      alive = false;
    };
    // `keys` — это и есть повод спросить заново: ключ лёг на устройство или
    // ушёл с него.
  }, [spaceId, attempt, keys]);

  const recheck = useCallback(() => setAttempt((count) => count + 1), []);

  return { presence, recheck };
}
